/**
 * Web Push client helpers (Phase 4 driver PWA).
 *
 * Registers the global Service Worker (with updateViaCache:'none' so the browser
 * never serves a stale sw.js after a deploy), and manages the browser push
 * subscription against the backend push endpoints.
 */
import { api } from '@/lib/apiClient';

export type SubscribeResult = { ok: boolean; reason?: 'unsupported' | 'denied' | 'no-key' | 'error' };

export function isPushSupported(): boolean {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

export function notificationPermission(): NotificationPermission | 'unsupported' {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  try {
    // updateViaCache:'none' forces the browser to bypass the HTTP cache for the
    // SW script itself, so a redeployed sw.js is picked up immediately.
    return await navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' });
  } catch (e) {
    console.warn('Service worker registration failed', e);
    return null;
  }
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export async function getExistingSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

export async function subscribeToPush(): Promise<SubscribeResult> {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' };
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return { ok: false, reason: 'denied' };
  try {
    const { data } = await api.get('/push/public-key');
    const key = data?.publicKey;
    if (!key) return { ok: false, reason: 'no-key' };
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key),
      });
    }
    const json: any = sub.toJSON();
    await api.post('/push/subscriptions', { endpoint: json.endpoint, keys: json.keys });
    localStorage.setItem('push_subscribed_since', new Date().toISOString());
    return { ok: true };
  } catch (e) {
    console.warn('Push subscribe failed', e);
    return { ok: false, reason: 'error' };
  }
}

export async function unsubscribeFromPush(): Promise<void> {
  if (!isPushSupported()) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      const json: any = sub.toJSON();
      try { await api.delete('/push/subscriptions', { data: { endpoint: json.endpoint, keys: json.keys } }); } catch {}
      await sub.unsubscribe();
    }
    localStorage.removeItem('push_subscribed_since');
  } catch (e) {
    console.warn('Push unsubscribe failed', e);
  }
}
